// ABOUTME: Component that dynamically renders authentication fields for relation-specific shipper credentials
// ABOUTME: based on shipper type (e.g., api_key/api_secret for innosend, username/password/clientId for gls)

import { Box, InputBase, IconButton } from "@mui/material";
import { useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";
import DeleteIcon from '@mui/icons-material/Delete';
import { InputDropdown } from "../../../../theme/dropdowns/InputDropdown";
import { useTranslation } from "react-i18next";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useGetConfigInfoQuery } from "../../../../../newapi/shipper/shipperSlice";

export const SHIPPER_AUTH_FIELDS = {
    innosend: [
        { name: 'api_key', type: 'text', placeholder: 'api_key' },
        { name: 'api_secret', type: 'text', placeholder: 'api_secret' }
    ],
    innosendv2: [
        { name: 'api_key', type: 'text', placeholder: 'api_token' }
    ],
    zineps: [
        { name: 'api_key', type: 'text', placeholder: 'client_id' },
        { name: 'api_secret', type: 'text', placeholder: 'client_secret' }
    ],
    gls: [
        { name: 'username', type: 'text', placeholder: 'username' },
        { name: 'password', type: 'password', placeholder: 'password' },
        { name: 'clientId', type: 'text', placeholder: 'client_id' }
    ],
    hst: [
        { name: 'login', type: 'text', placeholder: 'login_name' },
        { name: 'password', type: 'password', placeholder: 'password' },
        { name: 'customerId', type: 'text', placeholder: 'customer_id' }
    ],
    vinologix: [
        { name: 'accessToken', type: 'text', placeholder: 'access_token' },
        // Sender addresses and store fronts belong to the relation's own VLX Flow account; both are
        // optional and fall back to the shipper wide defaults when left empty. The options are loaded
        // from the config info with the relation's saved key; until then a plain id can be typed.
        { name: 'senderAddress', type: 'text', placeholder: 'vinologix_sender_address', optional: true, configInfoOptions: 'senderAddresses' },
        { name: 'storeFront', type: 'text', placeholder: 'storefront', optional: true, configInfoOptions: 'storeFronts' },
    ],
    wegrow: [
        { name: 'api_key', type: 'text', placeholder: 'api_key' }
    ]
};

// One relation row. Shipper types with configInfoOptions fields load the option lists with the
// relation's own saved key, so the dropdowns show that account's addresses.
const RelationAuthRow = ({
    shipperId,
    authFields,
    relationKey,
    index,
    relationOptions,
    onRelationKeyChange,
    onDeleteRelationKey
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const usesConfigInfo = authFields.some(field => field.configInfoOptions);
    const { data: configInfo } = useGetConfigInfoQuery(
        usesConfigInfo && shipperId && relationKey.id ? { id: shipperId, relationId: relationKey.id } : skipToken
    );

    const renderField = (field) => {
        const options = field.configInfoOptions ? configInfo?.configInfo?.[field.configInfoOptions] : null;
        const hasOptions = Array.isArray(options) && options.length > 0;

        return (
            <Box
                key={field.name}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    background: colors.grey[100],
                    borderRadius: 5,
                    height: 40,
                    flex: 1,
                    pl: hasOptions ? 0.5 : 2,
                }}
            >
                {hasOptions ?
                    <InputDropdown
                        key={`${field.name}-${relationKey[field.name]}`}
                        name={field.name}
                        noSelection={t(field.placeholder)}
                        displayName="key"
                        options={options}
                        selected={options.findIndex(option => option.value === relationKey[field.name])}
                        onChange={(selected) => onRelationKeyChange(field.name, index, options[selected].value)}
                        closeOnSelection
                    />
                :
                    <InputBase
                        height={32}
                        type={field.type}
                        value={relationKey[field.name] ?? ""}
                        onChange={(e) => onRelationKeyChange(field.name, index, e.target.value)}
                        placeholder={t(field.placeholder)}
                        sx={{
                            flex: 1,
                        }}
                    />
                }
            </Box>
        );
    };

    // Credential fields sit next to the relation; config info fields get a row of their own so a
    // token input is not squeezed between two address dropdowns.
    const credentialFields = authFields.filter(field => !field.configInfoOptions);
    const configInfoFields = authFields.filter(field => field.configInfoOptions);

    const row = (
        <Box
            sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
            }}
        >
            {/* Relation Dropdown */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    background: colors.grey[100],
                    borderRadius: 5,
                    height: 40,
                    flex: 1,
                    pl: 0.5,
                    maxWidth: 250,
                }}
            >
                <InputDropdown
                    key={`relation-${index}`}
                    name="relationId"
                    noSelection={t('choose_option')}
                    displayName="name"
                    searchOptions={{
                        enabled: true,
                        placeholder: t("search_relation_placeholder")
                    }}
                    styles={{
                        minWidth: 300
                    }}
                    options={relationOptions ?? []}
                    selected={relationOptions.findIndex(option => option.id === relationKey.id)}
                    onChange={(selected) => onRelationKeyChange("id", index, relationOptions[selected].id)}
                    closeOnSelection
                />
            </Box>

            {/* Dynamic Auth Fields */}
            {credentialFields.map(renderField)}

            {/* Delete Button */}
            <IconButton
                type="button"
                onClick={() => onDeleteRelationKey(index)}
                sx={{
                    color: colors.red[400]
                }}
            >
                <DeleteIcon/>
            </IconButton>
        </Box>
    );

    if (configInfoFields.length === 0) {
        return row;
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                boxShadow: `0px 2px 4px ${theme.palette.mode === "dark" ? null : colors.grey[200]}`,
                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                p: 2,
                borderRadius: 4,
            }}
        >
            {row}
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                }}
            >
                {configInfoFields.map(renderField)}
            </Box>
        </Box>
    );
};

const RelationAuthFields = ({
    shipperId,
    shipperType,
    relationKeys,
    relationOptions,
    onRelationKeyChange,
    onDeleteRelationKey
}) => {
    const authFields = SHIPPER_AUTH_FIELDS[shipperType] || [];

    if (!authFields.length) {
        return null;
    }

    return (
        <>
            {relationOptions && relationKeys.map((relationKey, index) => (
                <RelationAuthRow
                    key={`relationkeycontainer-${index}`}
                    shipperId={shipperId}
                    authFields={authFields}
                    relationKey={relationKey}
                    index={index}
                    relationOptions={relationOptions}
                    onRelationKeyChange={onRelationKeyChange}
                    onDeleteRelationKey={onDeleteRelationKey}
                />
            ))}
        </>
    );
};

export default RelationAuthFields;
